import { type Dispatch, type FC, type SetStateAction, useLayoutEffect, useState } from "react"

import { toggleStateArray } from "@forento/ui/utilities/array"
import { tw } from "@forento/ui/utilities/tailwind"

import Checkbox from "~/components/Checkbox"
import InputField from "~/components/InputField"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import RadioButtons from "~/components/RadioButtons"
import { usePlatform } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

type OptionId = "free" | "sign-in" | "paid" | "manual" | "membership" | "paid-and-membership" | "bokadero-member"
type Props = {
	type: "course" | "video" | "downloadable" | "session"
	isSignInRequired?: boolean
	setSignInRequired?: Dispatch<SetStateAction<boolean>>
	price: string
	setPrice: Dispatch<string>
	isManualOnly: boolean
	setManualOnly: Dispatch<SetStateAction<boolean>>
	accessTimeMonths?: string | null
	setAccessTimeMonths?: Dispatch<SetStateAction<string | null>>
	isBokaderoMemberRequired: boolean
	setBokaderoMemberRequired: Dispatch<SetStateAction<boolean>>
	membershipIds: number[]
	setMembershipIds: Dispatch<SetStateAction<number[]>>
	isPaused: boolean
	setPaused: Dispatch<SetStateAction<boolean>>
}
const ProductPricing: FC<Props> = ({
	type,
	isSignInRequired,
	setSignInRequired,
	price,
	setPrice,
	isManualOnly,
	setManualOnly,
	accessTimeMonths,
	setAccessTimeMonths,
	isBokaderoMemberRequired,
	setBokaderoMemberRequired,
	membershipIds,
	setMembershipIds,
	isPaused,
	setPaused,
}) => {
	const platform = usePlatform()!

	const [selectedOptionId, setSelectedOptionId] = useState<OptionId>(
		isManualOnly
			? "manual"
			: membershipIds.length > 0 && price.length > 0
				? "paid-and-membership"
				: price.length > 0
					? "paid"
					: membershipIds.length > 0
						? "membership"
						: platform.specialPlatform === "bokadero" && isBokaderoMemberRequired
							? "bokadero-member"
							: isSignInRequired
								? "sign-in"
								: "free",
	)

	// A downgraded platform keeps the option so an already manual-only product can be switched away from it.
	const [canSelectManualOnly] = useState(platform.plan.access.assignProducts || isManualOnly)

	const memberships = query.membership.list.useQuery()

	useLayoutEffect(() => {
		setManualOnly(selectedOptionId === "manual")
		if (selectedOptionId === "free") {
			setSignInRequired?.(false)
			setPrice("")
			setBokaderoMemberRequired?.(false)
			setMembershipIds([])
		} else if (selectedOptionId === "sign-in") {
			setSignInRequired?.(true)
			setPrice("")
			setBokaderoMemberRequired?.(false)
			setMembershipIds([])
		} else if (selectedOptionId === "paid") {
			setSignInRequired?.(false)
			setBokaderoMemberRequired?.(false)
			setMembershipIds([])
		} else if (selectedOptionId === "manual") {
			setSignInRequired?.(false)
			setPrice("")
			setBokaderoMemberRequired?.(false)
			setMembershipIds([])
		} else if (selectedOptionId === "bokadero-member") {
			setSignInRequired?.(false)
			setPrice("")
			setBokaderoMemberRequired?.(true)
			setMembershipIds([])
		} else if (selectedOptionId === "membership") {
			setSignInRequired?.(false)
			setPrice("")
			setBokaderoMemberRequired?.(false)
		} else if (selectedOptionId === "paid-and-membership") {
			setSignInRequired?.(false)
			setBokaderoMemberRequired?.(false)
		}
	}, [
		selectedOptionId,
		setAccessTimeMonths,
		setBokaderoMemberRequired,
		setManualOnly,
		setMembershipIds,
		setPrice,
		setSignInRequired,
	])

	const options: { id: OptionId; label: string; description: string }[] = [
		{ id: "free", label: "Free", description: `Your ${type} will be available to everyone for free.` },
		...(setSignInRequired !== undefined
			? [
					{
						id: "sign-in" as const,
						label: "Signed in",
						description: `Your ${type} will be available to signed in users.`,
					},
				]
			: []),
		{
			id: "paid",
			label: "Paid",
			description: `Your ${type} will be available to paid users (or if manually added by you).`,
		},
		...(canSelectManualOnly
			? [
					{
						id: "manual" as const,
						label: "Manual only",
						description: `Your ${type} can't be bought. It's only available to users you assign it to, from the dashboard or through an integration.`,
					},
				]
			: []),
		...(platform.specialPlatform === "bokadero"
			? [
					{
						id: "bokadero-member" as const,
						label: "Bokadero member",
						description: `Your ${type} will be available to Bokadero Arena members.`,
					},
				]
			: []),
		{
			id: "membership",
			label: "Membership",
			description: `Your ${type} will be available to members of a specific membership.`,
		},
		{
			id: "paid-and-membership",
			label: "Paid and membership",
			description: `Your ${type} will be available to paid users and members of a specific membership.`,
		},
	]

	const sectionClass = tw`flex flex-col gap-4`
	const sectionHeaderClass = tw`flex flex-col gap-1`
	const sectionTitleClass = tw`text-lg font-semibold`

	const descriptionClass = tw`text-light text-sm`

	const contentSectionClass = tw`mt-2 flex flex-col gap-4`
	const contentHeaderClass = tw`flex flex-col gap-1`
	const contentTitleClass = tw`font-semibold`
	const contentCardClass = tw`border-component-border flex flex-col gap-3 rounded-xl border bg-white p-5`

	return (
		<div className="flex flex-col gap-8">
			<div className={sectionClass}>
				<div className={sectionHeaderClass}>
					<h3 className={sectionTitleClass}>Product status</h3>
					<p className={descriptionClass}>Control whether your {type} is available for purchase</p>
				</div>

				<RadioButtons
					value={isPaused ? "unlisted" : "public"}
					onChange={value => setPaused(value === "unlisted")}
					options={[
						{
							value: "public",
							label: "Public",
							description: `Your ${type} is available and visible to everyone.`,
						},
						{
							value: "unlisted",
							label: "Unlisted",
							description: `Your ${type} will be unlisted for new enrollments but remains visible to existing students.`,
						},
					]}
				/>
			</div>

			<div className={sectionClass}>
				<div className={sectionHeaderClass}>
					<h3 className={sectionTitleClass}>Access control</h3>
					<p className={descriptionClass}>Choose how users can access your {type}</p>
				</div>

				<RadioButtons
					value={selectedOptionId}
					onChange={value => setSelectedOptionId(options.find(option => option.id === value)?.id ?? "free")}
					options={options.map(option => ({
						value: option.id,
						label: option.label,
						description: option.description,
					}))}
				/>

				{(selectedOptionId === "paid" || selectedOptionId === "paid-and-membership") && (
					<div className={contentSectionClass}>
						<div className={contentHeaderClass}>
							<h4 className={contentTitleClass}>Pricing settings</h4>
						</div>
						<div className={contentCardClass}>
							<InputField
								label={`Price (${platform.currency})`}
								value={price}
								onChange={setPrice}
								inputType="number"
								placeholder="0.00"
							/>
							{accessTimeMonths !== undefined && setAccessTimeMonths !== undefined && (
								<div className="border-component-border flex flex-col gap-4 border-t pt-4">
									<Checkbox
										label="Limit access time"
										isChecked={accessTimeMonths !== null}
										onChange={() => setAccessTimeMonths(current => (current === null ? "3" : null))}
									/>
									<InputField
										label="Access time (months)"
										value={accessTimeMonths ?? ""}
										onChange={setAccessTimeMonths}
										disabled={accessTimeMonths === null}
										inputType="number"
									/>
								</div>
							)}
						</div>
					</div>
				)}

				{(selectedOptionId === "membership" || selectedOptionId === "paid-and-membership") && (
					<div className={contentSectionClass}>
						<div className={contentHeaderClass}>
							<h4 className={contentTitleClass}>Membership access</h4>
							<p className={descriptionClass}>Select which memberships will have access to this {type}</p>
						</div>

						<div className={contentCardClass}>
							{memberships.error ? (
								<p>Failed to load memberships.</p>
							) : !memberships.data ? (
								<PartialLoadingPage />
							) : (
								memberships.data.map(membership => (
									<div
										key={membership.id}
										className="border-component-border bg-component-background hover:border-primary rounded-lg border p-3 transition-colors hover:bg-white"
									>
										<Checkbox
											isChecked={membershipIds.includes(membership.id)}
											label={membership.title}
											onChange={() => toggleStateArray(setMembershipIds, membership.id)}
										/>
									</div>
								))
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	)
}

export default ProductPricing
